import { useContext, useEffect } from 'react';
import { SeoCollectorContext, type HeadTags } from '@/ssr/seo';
import {
  SITE_URL,
  absoluteUrl,
  jsonLdDocument,
  pageStructuredData,
  type JsonLdNode,
} from '@/lib/structuredData';

export const SITE = SITE_URL;
export { absoluteUrl };

interface SeoOptions {
  /** Sahifa <title> */
  title: string;
  /** meta description (150-160 belgi tavsiya) */
  description?: string;
  /** Kanonik yo'l ("/problems") yoki to'liq URL. Berilmasa canonical o'zgarmaydi. */
  path?: string;
  /** 404 va shaxsiy sahifalar — indekslanmasin */
  noindex?: boolean;
  /**
   * Sahifaga xos schema.org tugunlari (lib/structuredData). Organization + WebSite
   * avtomatik qo'shiladi — har sahifada har turdan aynan bitta script.
   */
  jsonLd?: JsonLdNode[];
}

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

const LD_SELECTOR = 'script[type="application/ld+json"]';

/**
 * <head> dagi JSON-LD ni kerakli to'plamga moslaydi: `data-ld` (tur) bo'yicha bor
 * script yangilanadi, ortiqchasi va dublikati o'chiriladi, yo'g'i qo'shiladi.
 * Hydration'da prerender yozgan scriptlar qayta ishlatiladi — ikkinchi nusxa paydo bo'lmaydi.
 */
function syncJsonLd(nodes: JsonLdNode[]) {
  const wanted = new Map(nodes.map((n) => [n['@type'], JSON.stringify(jsonLdDocument(n))]));
  const seen = new Set<string>();
  document.head.querySelectorAll<HTMLScriptElement>(LD_SELECTOR).forEach((el) => {
    const type = el.dataset.ld;
    const json = type ? wanted.get(type) : undefined;
    if (!type || json === undefined || seen.has(type)) {
      el.remove();
      return;
    }
    seen.add(type);
    // Prerender '<' ni \u003c qilib escape qiladi — matn emas, qiymat solishtiriladi
    let same = false;
    try {
      same = JSON.stringify(JSON.parse(el.textContent ?? '')) === json;
    } catch {
      /* buzilgan — qayta yoziladi */
    }
    if (!same) el.textContent = json;
  });
  for (const [type, json] of wanted) {
    if (seen.has(type)) continue;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.dataset.ld = type;
    el.textContent = json;
    document.head.appendChild(el);
  }
}

/**
 * Sahifa uchun SEO meta teglarini (title, description, canonical, OG/Twitter)
 * o'rnatadi. Prerender paytida (server) qiymatlar SeoCollectorContext ga yoziladi
 * va HTML <head> ga tushadi; brauzerda SPA navigatsiyasida DOM yangilanadi.
 */
export function useSeo({ title, description, path, noindex, jsonLd }: SeoOptions) {
  const collector = useContext(SeoCollectorContext);
  const nodes = pageStructuredData(jsonLd);
  if (collector) {
    const tags: HeadTags = { title, description, path, noindex, jsonLd: nodes };
    Object.assign(collector, tags);
  }
  // Effekt faqat tarkib o'zgarganda qayta ishlasin (har renderda yangi massiv)
  const ldKey = JSON.stringify(nodes);

  useEffect(() => {
    document.title = title;
    upsertMeta('property', 'og:title', title);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow');

    if (description) {
      upsertMeta('name', 'description', description);
      upsertMeta('property', 'og:description', description);
      upsertMeta('name', 'twitter:description', description);
    }

    if (path) {
      const url = absoluteUrl(path);
      let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', url);
      upsertMeta('property', 'og:url', url);
    }
  }, [title, description, path, noindex]);

  useEffect(() => {
    syncJsonLd(JSON.parse(ldKey) as JsonLdNode[]);
  }, [ldKey]);
}
